/**
 * Web retention offer (plan §6.4): annual 9.99 first year. The Home card calls
 * useRetentionOffer(); evaluation runs on mount and on window focus.
 */
import { createContext, useContext, useEffect } from 'react';
import type { RetentionEvaluation, RetentionPlacement, RetentionSignal } from '@/services/api';
import { usePaywallPrices } from './usePaywallPrices';

export interface RetentionAPI {
  evaluation?: RetentionEvaluation;
  evaluate(signal: RetentionSignal, placement: RetentionPlacement): Promise<RetentionEvaluation | undefined>;
  claim(placement: RetentionPlacement): Promise<void>;
  claiming: boolean;
  error?: string;
}

export const RetentionContext = createContext<RetentionAPI | null>(null);

export interface RetentionOffer { productID: string; price: string; renewal: string }

export function useRetentionOffer(): { offer?: RetentionOffer; claim: () => Promise<void>; claiming: boolean; error?: string } {
  const ctx = useContext(RetentionContext);
  if (!ctx) throw new Error('useRetentionOffer outside PaywallProvider');
  const prices = usePaywallPrices();
  const { evaluate } = ctx;
  useEffect(() => {
    void evaluate('refresh', 'home_quick_action');
    const onFocus = () => { void evaluate('refresh', 'home_quick_action'); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [evaluate]);
  const p = prices.data;
  const offer = ctx.evaluation?.eligible && p
    ? { productID: ctx.evaluation.product_id, price: p.retentionAnnual.display, renewal: (p.retentionRenewal ?? p.annual).display }
    : undefined;
  return { offer, claim: () => ctx.claim('home_quick_action'), claiming: ctx.claiming, error: ctx.error };
}
